import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useConfig } from '@context/ConfigContext';
import Button from '@components/shared/Button/Button';
import emailjs from '@emailjs/browser';
import styles from './Contact.module.css';

interface FormData {
  name: string;
  email: string;
  phone: string;
  message: string;
  honeypot: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
}

const Contact: React.FC = () => {
  const { config } = useConfig();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    message: '',
    honeypot: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  if (!config || !config.components.contact.enabled) return null;

  const { contact: contactInfo } = config;

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check honeypot
    if (formData.honeypot) {
      return;
    }

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // EmailJS configuration
        const serviceId = 'service_kdty70c';      // Replace with your Service ID
        const templateId = 'template_icmda7c';    // Replace with your Template ID
        const publicKey = 'RZfcCirnjkAAuLwf4';      // Replace with your Public Key

      // Prepare template parameters
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        message: formData.message,
        to_email: 'danwhitewebsolutions@outlook.com',
      };

      // Send email using EmailJS
      await emailjs.send(
        serviceId,
        templateId,
        templateParams,
        publicKey
      );

      // Success
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '', honeypot: '' });
    } catch (error) {
      console.error('EmailJS submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <section id="contact" className={styles.contact}>
      <div className={styles.container}>
        <motion.div
          className={styles.content}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <motion.div className={styles.header} variants={itemVariants}>
            <h2 className={styles.title}>Get In Touch</h2>
            <div className={styles.divider}></div>
            <p className={styles.subtitle}>
              Have a question or ready to start your project? We'd love to hear from you.
            </p>
          </motion.div>

          <div className={styles.contactWrapper}>
            <motion.div className={styles.formSection} variants={itemVariants}>
              <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.formGroup}>
                  <label htmlFor="name" className={styles.label}>
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
                    placeholder="Your name"
                  />
                  {errors.name && <span className={styles.error}>{errors.name}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="email" className={styles.label}>
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                    placeholder="your.email@example.com"
                  />
                  {errors.email && <span className={styles.error}>{errors.email}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="phone" className={styles.label}>
                    Phone *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`${styles.input} ${errors.phone ? styles.inputError : ''}`}
                    placeholder="Your phone number"
                  />
                  {errors.phone && <span className={styles.error}>{errors.phone}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="message" className={styles.label}>
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    className={`${styles.textarea} ${errors.message ? styles.inputError : ''}`}
                    placeholder="Tell us about your project..."
                  />
                  {errors.message && <span className={styles.error}>{errors.message}</span>}
                </div>

                <label htmlFor="honeypot" className={styles.honeypot} aria-hidden="true">
                  Leave this field empty
                  <input
                    type="text"
                    id="honeypot"
                    name="honeypot"
                    value={formData.honeypot}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </label>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>

                {submitStatus === 'success' && (
                  <motion.div
                    className={styles.successMessage}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <i className="fas fa-check-circle" />
                    <p>Thank you! Your message has been sent successfully.</p>
                  </motion.div>
                )}

                {submitStatus === 'error' && (
                  <motion.div
                    className={styles.errorMessage}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <i className="fas fa-exclamation-circle" />
                    <p>Oops! Something went wrong. Please try again.</p>
                  </motion.div>
                )}
              </form>
            </motion.div>

            <motion.div className={styles.infoSection} variants={itemVariants}>
              <div className={styles.infoCard}>
                <h3 className={styles.infoTitle}>Contact Information</h3>

                <div className={styles.infoItem}>
                  <i className="fas fa-phone" />
                  <div>
                    <p className={styles.infoLabel}>Phone</p>
                    <a href={`tel:${contactInfo.phoneRaw}`} className={styles.infoValue}>
                      {contactInfo.phone}
                    </a>
                  </div>
                </div>

                <div className={styles.infoItem}>
                  <i className="fas fa-envelope" />
                  <div>
                    <p className={styles.infoLabel}>Email</p>
                    <a href={`mailto:${contactInfo.email}`} className={styles.infoValue}>
                      {contactInfo.email}
                    </a>
                  </div>
                </div>

                <div className={styles.infoItem}>
                  <i className="fas fa-map-marker-alt" />
                  <div>
                    <p className={styles.infoLabel}>Address</p>
                    <p className={styles.infoValue}>{contactInfo.address}</p>
                  </div>
                </div>

                {contactInfo.whatsappEnabled && contactInfo.whatsapp && (
                  <a
                    href={`https://wa.me/${contactInfo.whatsapp.replace(/\+/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.whatsappButton}
                  >
                    <i className="fab fa-whatsapp" />
                    Chat on WhatsApp
                  </a>
                )}
              </div>

              {contactInfo.map.enabled && contactInfo.map.embedUrl && (
                <div className={styles.mapContainer}>
                  <iframe
                    src={contactInfo.map.embedUrl}
                    className={styles.map}
                    allowFullScreen
                    loading="lazy"
                    title="Business Location"
                  />
                </div>
              )}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;